// Bounded page-memory for Task composer retry correlation. Map insertion order is the touch order. Only the
// bounded draft, exact PendingTaskRequest, and its delivery certainty survive a modal close; refusal notes,
// queued-run observers, streams, and transport/UI state remain in the modal controller.

import { boundRetainedDraft } from './draft-text-limit.ts';
import type {
  PendingTaskRequest,
  TaskComposerRetention,
  TaskComposerRetentionEvent,
} from '../surfaces/task-request-identity.ts';

export const TASK_DRAFT_REGISTRY_LIMIT = 50;

export type TaskDraftRegistry = ReadonlyMap<string, TaskComposerRetention>;

function hasEvictableDraft(current: TaskDraftRegistry, activeTaskId: string | null): boolean {
  for (const [candidate, retention] of current) {
    if (candidate !== activeTaskId && retention.status === 'draft') return true;
  }
  return false;
}

/** A POST may start only when its exact correlation can be retained before transport begins. */
export function canRetainTaskRequest(
  current: TaskDraftRegistry,
  taskId: string,
  activeTaskId: string | null,
): boolean {
  return (
    current.has(taskId) ||
    current.size < TASK_DRAFT_REGISTRY_LIMIT ||
    hasEvictableDraft(current, activeTaskId)
  );
}

function exactPending(taskId: string, draft: string, pending: PendingTaskRequest): boolean {
  return (
    pending.requestId.trim().length > 0 &&
    pending.taskId === taskId &&
    pending.message === draft.trim() &&
    pending.message.length > 0
  );
}

function reduceTaskRetention(
  current: TaskComposerRetention | undefined,
  taskId: string,
  event: TaskComposerRetentionEvent,
): TaskComposerRetention | null | undefined {
  if (event.kind === 'draft_changed') {
    const draft = boundRetainedDraft(event.draft);
    if (draft.length === 0) return null;
    const reusable = current?.pendingRequest;
    return reusable !== null &&
      reusable !== undefined &&
      exactPending(taskId, draft, reusable) &&
      current?.status === 'uncertain'
      ? { draft, pendingRequest: reusable, status: 'uncertain' }
      : { draft, pendingRequest: null, status: 'draft' };
  }

  if (event.kind === 'request_started') {
    const draft = boundRetainedDraft(event.draft);
    if (!exactPending(taskId, draft, event.pendingRequest)) {
      return draft.length > 0 ? { draft, pendingRequest: null, status: 'draft' } : null;
    }
    return { draft, pendingRequest: event.pendingRequest, status: 'in_flight' };
  }

  if (current?.pendingRequest?.requestId !== event.pendingRequest.requestId) return undefined;
  if (event.kind === 'request_accepted') return null;
  if (event.kind === 'request_unknown') return { ...current, status: 'uncertain' };
  return { draft: current.draft, pendingRequest: null, status: 'draft' };
}

/** Reduce one exact controller event. Ordinary drafts are evictable; unresolved correlations never are. */
export function reduceTaskDraftRegistry(
  current: TaskDraftRegistry,
  taskId: string,
  event: TaskComposerRetentionEvent,
  activeTaskId: string | null,
): TaskDraftRegistry {
  const retained = reduceTaskRetention(current.get(taskId), taskId, event);
  if (retained === undefined) return current;
  const next = new Map(current);
  next.delete(taskId);
  if (retained !== null) next.set(taskId, retained);

  while (next.size > TASK_DRAFT_REGISTRY_LIMIT) {
    let oldestEvictable: string | undefined;
    for (const [candidate, retention] of next) {
      if (candidate === activeTaskId || retention.status !== 'draft') continue;
      oldestEvictable = candidate;
      break;
    }
    // All retained entries carry unresolved request identity. Refuse to retain the new ordinary draft;
    // request admission separately observes the same condition and therefore refuses before POST.
    if (oldestEvictable === undefined) return current;
    next.delete(oldestEvictable);
  }
  return next;
}
